

import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
// Chakra imports
import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
// Custom components
import { HSeparator } from "components/separator/Separator";
import DefaultAuth from "layouts/auth/Default";
// Services
import { login } from "services/authService";
// Utils
import { getDashboardRoute } from "utils/routeHelpers";
// Assets
import illustration from "assets/img/auth/auth.png";
import { FcGoogle } from "react-icons/fc";
import { MdOutlineRemoveRedEye } from "react-icons/md";
import { RiEyeCloseLine } from "react-icons/ri";

function SignIn() {
  const navigate = useNavigate();
  const toast = useToast();

  // Chakra color mode
  const textColor = useColorModeValue("navy.700", "white");
  const textColorSecondary = "gray.400";
  const textColorBrand = useColorModeValue("brand.500", "white");
  const brandStars = useColorModeValue("brand.500", "brand.400");
  const googleBg = useColorModeValue("secondaryGray.300", "whiteAlpha.200");
  const googleText = useColorModeValue("navy.700", "white");
  const googleHover = useColorModeValue(
    { bg: "gray.200" },
    { bg: "whiteAlpha.300" }
  );
  const googleActive = useColorModeValue(
    { bg: "secondaryGray.300" },
    { bg: "whiteAlpha.200" }
  );

  // Form state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [show, setShow] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [rateLimitCountdown, setRateLimitCountdown] = useState(null);

  const handleClick = () => setShow(!show);

  // Countdown timer for rate limit
  React.useEffect(() => {
    if (errors.retryAfter && errors.retryAfter > 0) {
      // Initialize countdown with retryAfter value (in seconds)
      setRateLimitCountdown(errors.retryAfter);
      
      const timer = setInterval(() => {
        setRateLimitCountdown((prev) => {
          if (prev === null || prev <= 1) {
            clearInterval(timer);
            setErrors((prevErrors) => {
              const newErrors = { ...prevErrors };
              delete newErrors.rateLimit;
              delete newErrors.retryAfter;
              return newErrors;
            });
            return null;
          }
          return prev - 1;
        });
      }, 1000);
      
      return () => clearInterval(timer);
    } else {
      // Reset countdown if retryAfter is not set
      setRateLimitCountdown(null);
    }
  }, [errors.retryAfter]);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setIsLoading(true);

    // Basic validation
    const newErrors = {};
    if (!email) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = "Email is invalid";
    }
    if (!password) {
      newErrors.password = "Password is required";
    } else if (password.length < 8) {
      newErrors.password = "Password must be at least 8 characters";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setIsLoading(false);
      return;
    }

    try {
      const result = await login(email, password);

      // Debug logging
      if (process.env.NODE_ENV === 'development') {
        console.log('Login result:', result);
      }

      if (result.success) {
        // Show success message
        toast({
          title: "Login successful",
          description: result.message || "Welcome back!",
          status: "success",
          duration: 3000,
          isClosable: true,
        });

        // Redirect to role-specific dashboard
        const dashboardRoute = getDashboardRoute(result.userRole);
        navigate(dashboardRoute);
      } else {
        // Handle error - prioritize backend error message
        // For rate limit: use result.error (from backend error field)
        // For others: Check response.error first (from backend), then result.error, then result.message
        const backendError = result.response?.error || result.error || result.message;
        const errorMessage = backendError || "Login failed. Please try again.";
        
        // Set field-specific errors if available
        if (result.code === 401) {
          // Use backend message if available, otherwise use default
          const message = backendError || "Invalid email or password. Please check your credentials and try again.";
          setErrors({ general: message });
          toast({
            title: "Login failed",
            description: message,
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        } else if (result.code === 422) {
          // Validation errors
          const message = backendError || "Validation error. Please check your input.";
          if (result.response?.errors) {
            setErrors(result.response.errors);
          } else {
            setErrors({ general: message });
          }
          toast({
            title: "Validation Error",
            description: message,
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        } else if (result.code === 429) {
          // Handle rate limit with retry information
          // retryAfter should be extracted from Retry-After header by api.js
          const retryAfter = result.retryAfter || null;
          
          // For rate limit, use the error field from backend (not message)
          // Priority: result.error (from backend error field) > result.response?.error > result.message
          const retryMessage = result.error || result.response?.error || result.message || "Rate limit exceeded. Please try again later.";
          
          setErrors({ 
            general: retryMessage,
            rateLimit: true,
            retryAfter: retryAfter,
          });
          
          // Show extended toast for rate limit
          toast({
            title: "Rate Limit Exceeded",
            description: retryMessage,
            status: "warning",
            duration: retryAfter ? Math.min(retryAfter * 1000, 30000) : 10000, // Max 30 seconds for toast
            isClosable: true,
          });
        } else {
          // For other errors, use backend message
          setErrors({ general: errorMessage });
          toast({
            title: "Login failed",
            description: errorMessage,
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        }
      }
    } catch (error) {
      // Handle network errors and other exceptions
      // The login function should have already handled most errors,
      // but catch any unexpected ones here
      let errorMessage = "An unexpected error occurred. Please try again.";
      
      // Check if it's a network error
      if (error.message === "Failed to fetch" || 
          error.message.includes("NetworkError") || 
          error.name === "TypeError" ||
          error.isNetworkError) {
        errorMessage = "Network error. Please check your internet connection and try again.";
      } else if (error.message && error.message !== "Failed to fetch") {
        // Use the actual error message if it's not a generic fetch error
        errorMessage = error.message;
      }
      
      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      setErrors({ general: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <DefaultAuth illustrationBackground={illustration} image={illustration}>
      <Flex
        maxW={{ base: "100%", md: "max-content" }}
        w='100%'
        mx={{ base: "auto", lg: "0px" }}
        me='auto'
        h='100%'
        alignItems='start'
        justifyContent='center'
        mb={{ base: "30px", md: "60px" }}
        px={{ base: "25px", md: "0px" }}
        mt={{ base: "40px", md: "14vh" }}
        flexDirection='column'>
        <Box me='auto'>
          <Heading color={textColor} fontSize='36px' mb='10px'>
            Sign In
          </Heading>
          <Text
            mb='36px'
            ms='4px'
            color={textColorSecondary}
            fontWeight='400'
            fontSize='md'>
            Enter your email and password to sign in!
          </Text>
        </Box>
        <Flex
          zIndex='2'
          direction='column'
          w={{ base: "100%", md: "420px" }}
          maxW='100%'
          background='transparent'
          borderRadius='15px'
          mx={{ base: "auto", lg: "unset" }}
          me='auto'
          mb={{ base: "20px", md: "auto" }}>
          <Button
            fontSize='sm'
            me='0px'
            mb='26px'
            py='15px'
            h='50px'
            borderRadius='16px'
            bg={googleBg}
            color={googleText}
            fontWeight='500'
            _hover={googleHover}
            _active={googleActive}
            _focus={googleActive}>
            <Icon as={FcGoogle} w='20px' h='20px' me='10px' />
            Sign in with Google
          </Button>
          <Flex align='center' mb='25px'>
            <HSeparator />
            <Text color='gray.400' mx='14px'>
              or
            </Text>
            <HSeparator />
          </Flex>
          <form onSubmit={handleSubmit}>
            {errors.general && (
              <Box
                mb='20px'
                p='12px'
                borderRadius='8px'
                bg={errors.rateLimit ? 'orange.50' : 'red.50'}
                border='1px solid'
                borderColor={errors.rateLimit ? 'orange.200' : 'red.200'}>
                <Text color={errors.rateLimit ? 'orange.600' : 'red.600'} fontSize='sm'>
                  {errors.general}
                  {errors.rateLimit && rateLimitCountdown > 0 && (
                    <Text as='span' fontWeight='bold' ml='4px'>
                      (Retry in {rateLimitCountdown} seconds)
                    </Text>
                  )}
                </Text>
              </Box>
            )}
            <FormControl isInvalid={!!errors.email} mb='24px'>
              <FormLabel
                display='flex'
                ms='4px'
                fontSize='sm'
                fontWeight='500'
                color={textColor}
                mb='8px'>
                Email<Text color={brandStars}>*</Text>
              </FormLabel>
              <Input
                isRequired={true}
                variant='auth'
                fontSize='sm'
                ms={{ base: "0px", md: "0px" }}
                type='email'
                placeholder='mail@example.com'
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fontWeight='500'
                size='lg'
              />
              {errors.email && (
                <Text color='red.500' fontSize='xs' mt='4px' ms='4px'>
                  {errors.email}
                </Text>
              )}
            </FormControl>
            <FormControl isInvalid={!!errors.password} mb='24px'>
              <FormLabel
                ms='4px'
                fontSize='sm'
                fontWeight='500'
                color={textColor}
                display='flex'>
                Password<Text color={brandStars}>*</Text>
              </FormLabel>
              <InputGroup size='md'>
                <Input
                  isRequired={true}
                  fontSize='sm'
                  placeholder='Min. 8 characters'
                  size='lg'
                  type={show ? "text" : "password"}
                  variant='auth'
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <InputRightElement display='flex' alignItems='center' mt='4px'>
                  <Icon
                    color={textColorSecondary}
                    _hover={{ cursor: "pointer" }}
                    as={show ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                    onClick={handleClick}
                  />
                </InputRightElement>
              </InputGroup>
              {errors.password && (
                <Text color='red.500' fontSize='xs' mt='4px' ms='4px'>
                  {errors.password}
                </Text>
              )}
            </FormControl>
            <Flex justifyContent='space-between' align='center' mb='24px'>
              <FormControl display='flex' alignItems='center'>
                <Checkbox
                  id='remember-login'
                  colorScheme='brandScheme'
                  me='10px'
                  isChecked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                <FormLabel
                  htmlFor='remember-login'
                  mb='0'
                  fontWeight='normal'
                  color={textColor}
                  fontSize='sm'>
                  Keep me logged in
                </FormLabel>
              </FormControl>
              <NavLink to='/auth/forgot-password'>
                <Text
                  color={textColorBrand}
                  fontSize='sm'
                  w='124px'
                  fontWeight='500'>
                  Forgot password?
                </Text>
              </NavLink>
            </Flex>
            <Button
              type='submit'
              fontSize='sm'
              variant='brand'
              fontWeight='500'
              w='100%'
              h='50'
              mb='24px'
              isLoading={isLoading}
              loadingText='Signing in...'
              disabled={isLoading || (errors.rateLimit && rateLimitCountdown > 0)}>
              {errors.rateLimit && rateLimitCountdown > 0
                ? `Try again in ${rateLimitCountdown}s`
                : 'Sign In'}
            </Button>
          </form>
        </Flex>
      </Flex>
    </DefaultAuth>
  );
}

export default SignIn;
